/**
 * Thin fetch wrapper. Centralises the API base URL, JSON handling, and error
 * normalisation so every call site stays a one-liner and errors look the same.
 */

import type { SearchParams } from "./types";

/**
 * API base URL. Empty by default → same-origin relative requests ("/api/..."),
 * which is what the dev proxy and the nginx production image expect. Override
 * with VITE_API_URL only for split-origin deployments.
 */
const API_BASE: string = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

/** Shape of the backend's uniform error body (ApiError). */
export interface ApiErrorBody {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  fieldErrors: { field: string; message: string }[];
}

/** Error thrown for any non-2xx response, carrying the parsed body when present. */
export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Core request helper. Merges headers correctly (caller headers win, but Accept
 * is always present), normalises errors, and returns parsed JSON (or undefined
 * for 204). Exported so the admin client can layer auth headers on top.
 */
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const { headers: initHeaders, ...rest } = init ?? {};
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      headers: { Accept: "application/json", ...(initHeaders ?? {}) },
    });
  } catch {
    throw new ApiError(0, "Network error — is the server running?");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const body = data as ApiErrorBody | undefined;
    throw new ApiError(response.status, body?.message ?? response.statusText, body);
  }
  return data as T;
}

/** JSON headers merged with any extra headers (e.g. the admin key). */
function jsonHeaders(extra?: HeadersInit): HeadersInit {
  return { "Content-Type": "application/json", ...(extra ?? {}) };
}

export const http = {
  get: <T>(path: string, headers?: HeadersInit) =>
    apiRequest<T>(path, { headers }),
  post: <T>(path: string, body: unknown, headers?: HeadersInit) =>
    apiRequest<T>(path, {
      method: "POST",
      headers: jsonHeaders(headers),
      body: JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown, headers?: HeadersInit) =>
    apiRequest<T>(path, {
      method: "PUT",
      headers: jsonHeaders(headers),
      body: JSON.stringify(body),
    }),
  del: <T>(path: string, headers?: HeadersInit) =>
    apiRequest<T>(path, { method: "DELETE", headers }),
};

/**
 * Build a query string from search parameters. Arrays are emitted as repeated
 * keys (?types=VIDEO&types=LINK), which the backend reads directly. Undefined,
 * null, and empty values are skipped so the URL stays clean and cache-friendly.
 */
export function buildSearchQuery(params: SearchParams): string {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  (params.types ?? []).forEach((t) => qs.append("types", t));
  (params.tags ?? []).forEach((t) => qs.append("tags", t));
  (params.collections ?? []).forEach((c) => qs.append("collections", c));
  if (params.scope) qs.set("scope", params.scope);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.sort) qs.set("sort", params.sort);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.size != null) qs.set("size", String(params.size));
  const s = qs.toString();
  return s ? `?${s}` : "";
}
